import { forwardRef, ReactNode } from "react";
import type {
  RadioButtonProps as ReactAriaRadioButtonProps,
  RadioGroupProps as ReactAriaRadioGroupProps,
} from "react-aria-components";
import {
  RadioButton as ReactAriaRadioButton,
  RadioField as ReactAriaRadioField,
  RadioGroup as ReactAriaRadioGroup,
} from "react-aria-components";
import { FieldError } from "react-hook-form";

import { cn } from "../utils";
import { NcFieldLayout, NcFieldLayoutProps, ncFieldLayoutVariants } from "./nc-field-layout";
import { NcIconTick } from "./nc-icons";
import { NcLabel, ncLabelClasses } from "./nc-label";

export interface NcRadioGroupProps extends ReactAriaRadioGroupProps {
  options: Array<{
    label: string;
    value: string;
    isDisabled?: boolean;
  }>;
  label: string;
  hideLabel?: boolean;
  description?: string;
  error?: FieldError;
  variant?: NcFieldLayoutProps["variant"];
}

export const NcRadioGroup = forwardRef<HTMLDivElement, NcRadioGroupProps>(
  (
    {
      label,
      hideLabel,
      description,
      options,
      error,
      isRequired,
      children,
      variant,
      className,
      ...props
    },
    ref
  ) => (
    <ReactAriaRadioGroup
      data-nc="NcRadioGroup"
      className={cn(ncFieldLayoutVariants(), className)}
      ref={ref}
      {...props}
    >
      <NcLabel isRequired={isRequired} hideLabel={hideLabel}>
        {label}
      </NcLabel>
      <NcFieldLayout variant={variant}>
        {children ? (
          <>{children}</>
        ) : (
          options.map(option => (
            <Radio key={option.value} value={option.value} isDisabled={option.isDisabled}>
              {option.label}
            </Radio>
          ))
        )}
      </NcFieldLayout>
      {description && <NcFieldLayout.Description>{description}</NcFieldLayout.Description>}
      <NcFieldLayout.ErrorMessage>{error?.message}</NcFieldLayout.ErrorMessage>
    </ReactAriaRadioGroup>
  )
);

interface RadioProps extends ReactAriaRadioButtonProps {
  value: string;
  isDisabled?: boolean;
  children: ReactNode;
}

const Radio = forwardRef<HTMLLabelElement, RadioProps>(
  ({ children, className, value, isDisabled, ...props }, ref) => {
    return (
      <ReactAriaRadioField
        data-nc="NcRadio"
        value={value}
        isDisabled={isDisabled}
        className="contents"
      >
        <ReactAriaRadioButton
          ref={ref}
          className={cn(
            "group rounded-md",
            // Flex, gap, alignment
            "flex max-w-max items-center gap-2 transition-transform duration-300",
            // Disabled
            "data-disabled:text-disabled data-disabled:cursor-not-allowed",
            // Focus visible
            "data-focus-visible:outline-main data-focus-visible:outline-2 data-focus-visible:outline-offset-2",
            className
          )}
          {...props}
        >
          <div
            className={cn(
              // Sizing, shape, border
              "bg-light text-light border-ui flex h-6 w-6 min-w-0 cursor-pointer items-center justify-center rounded-full border transition-colors duration-300",
              // Disabled
              "group-data-disabled:bg-disabled-light",
              // Selected (checked)
              "group-data-selected:bg-active",
              // Selected + disabled
              "group-data-disabled:group-data-selected:bg-disabled",
              // Indeterminate
              "group-data-indeterminate:bg-active",
              // Invalid
              "group-data-invalid:border-danger"
            )}
          >
            <NcIconTick className="h-4 scale-0 transition-all duration-300 ease-out group-data-selected:scale-100" />
          </div>
          <div className={cn(ncLabelClasses, "group-data-disabled:cursor-not-allowed")}>
            {children}
          </div>
        </ReactAriaRadioButton>
      </ReactAriaRadioField>
    );
  }
);
